import React, { useState, useEffect, useCallback } from 'react';
import { Post } from '@contentflow/shared-types';
import { useParams, useNavigate } from 'react-router-dom';
import RichEditor from './RichEditor';
import './Editor.css';

interface EditorProps {
  token: string;
  user: { id: string; email: string };
  onLogout: () => void;
  apiBaseUrl: string;
}

const Editor: React.FC<EditorProps> = ({ token, user, onLogout, apiBaseUrl }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    meta_description: '',
    featured_image_url: '',
    featured_image_base64: '',
    tags: '',
    uc_problem: '',
    uc_solution: '',
    uc_product_name: '',
    uc_product_url: '',
    uc_product_image_url: '',
    uc_source_subreddit: '',
    author: ''
  });

  const fetchPost = useCallback(async (postId: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${postId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setPost(data.data);
        console.log('Editor: Post data received:', data.data);
        console.log('Editor: Featured image base64 length:', data.data.featured_image_base64?.length || 0);
        console.log('Editor: Featured image base64 starts with:', data.data.featured_image_base64?.substring(0, 50));
        console.log('Editor: Featured image base64 is valid data URL?', data.data.featured_image_base64?.startsWith('data:'));
        setFormData({
          title: data.data.title || '',
          body: data.data.body || '',
          meta_description: data.data.meta_description || '',
          featured_image_url: data.data.featured_image_url || '',
          featured_image_base64: data.data.featured_image_base64 || '',
          tags: data.data.tags || '',
          uc_problem: data.data.uc_problem || '',
          uc_solution: data.data.uc_solution || '',
          uc_product_name: data.data.uc_product_name || '',
          uc_product_url: data.data.uc_product_url || '',
          uc_product_image_url: data.data.uc_product_image_url || '',
          uc_source_subreddit: data.data.uc_source_subreddit || '',
          author: data.data.author || ''
        });
      } else {
        setError(data.message || 'Failed to fetch post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (id) {
      fetchPost(id);
    }
  }, [id, fetchPost]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!post) return;

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          updated_at: new Date().toISOString()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPost(data.data);
        // Show success message briefly
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Post saved successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      } else {
        setError(data.message || 'Failed to save post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!post) return;

    setPublishing(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${post.id}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          status: 'published',
          published_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPost(data.data);
        // Show success message and redirect to dashboard
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Post published successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
          navigate('/cms');
        }, 2000);
      } else {
        setError(data.message || 'Failed to publish post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleBack = () => {
    navigate('/cms');
  };

  if (loading) {
    return (
      <div className="editor">
        <header className="editor-header">
          <div className="header-content">
            <h1>SolveSmartHome CMS</h1>
            <div className="user-info">
              <span>Welcome, {user.email}</span>
              <button onClick={onLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="editor-main">
          <div className="editor-content">
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading post...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className="editor">
        <header className="editor-header">
          <div className="header-content">
            <h1>SolveSmartHome CMS</h1>
            <div className="user-info">
              <span>Welcome, {user.email}</span>
              <button onClick={onLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="editor-main">
          <div className="editor-content">
            <div className="error-message">
              <h2>Error</h2>
              <p>{error}</p>
              <button onClick={handleBack} className="back-button">
                ← Back to Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="editor">
      <header className="editor-header">
        <div className="header-content">
          <h1>SolveSmartHome CMS</h1>
          <div className="user-info">
            <span>Welcome, {user.email}</span>
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="editor-main">
        <div className="editor-content">
          <div className="editor-header-section">
            <div className="editor-title-section">
              <h2>Edit Post</h2>
              <div className="post-status-info">
                <span className={`status-badge ${post.status}`}>
                  {post.status}
                </span>
                <span className="post-slug">/{post.slug}</span>
              </div>
            </div>

            <div className="editor-actions">
              <button onClick={handleBack} className="action-button secondary">
                ← Back to Dashboard
              </button>
              <button
                onClick={handleSave}
                className="action-button primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              {post.status === 'draft' && (
                <button
                  onClick={handlePublish}
                  className="action-button publish"
                  disabled={publishing}
                >
                  {publishing ? 'Publishing...' : 'Publish'}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="editor-form">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter post title..."
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="author">Author Name</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                placeholder="e.g. Alex Chen (Optional)"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="meta_description">Meta Description</label>
              <textarea
                id="meta_description"
                name="meta_description"
                value={formData.meta_description}
                onChange={handleInputChange}
                placeholder="Enter meta description for SEO..."
                className="form-textarea"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="featured_image_url">Featured Image URL</label>
              <input
                type="url"
                id="featured_image_url"
                name="featured_image_url"
                value={formData.featured_image_url}
                onChange={handleInputChange}
                placeholder="https://example.com/image.jpg"
                className="form-input"
              />
            </div>

            {(formData.featured_image_url || formData.featured_image_base64) && (
              <div className="form-group">
                <label>Current Featured Image</label>
                <div className="image-preview">
                  <img
                    src={formData.featured_image_base64 || formData.featured_image_url}
                    alt="Featured image preview"
                    className="preview-image"
                    onError={(e) => {
                      console.error('Editor: Image failed to load:', e);
                      const img = e.currentTarget;
                      const src = img.src;

                      // Log detailed error information
                      console.log('Editor: Image src length:', src.length);
                      console.log('Editor: Image src starts with:', src.substring(0, 50));
                      console.log('Editor: Image src ends with:', src.substring(src.length - 20));
                      console.log('Editor: Is valid data URL?', src.startsWith('data:'));

                      if (src.startsWith('data:')) {
                        const base64Data = src.split(',')[1];
                        if (base64Data) {
                          console.log('Editor: Base64 data length:', base64Data.length);
                          console.log('Editor: Base64 padding check:', base64Data.length % 4);
                          console.log('Editor: Contains invalid chars:', !/^[A-Za-z0-9+/]*={0,2}$/.test(base64Data));
                          console.log('Editor: Last 10 chars:', base64Data.substring(base64Data.length - 10));

                          // Check JPEG signature
                          try {
                            const binaryString = atob(base64Data);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                              bytes[i] = binaryString.charCodeAt(i);
                            }
                            const firstBytes = Array.from(bytes.slice(0, 10)).map(b => b.toString(16).padStart(2, '0')).join(' ');
                            console.log('Editor: First 10 bytes (hex):', firstBytes);

                            // Check for JPEG signature (FF D8 FF)
                            const isValidJPEG = bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
                            console.log('Editor: JPEG signature check:', isValidJPEG ? 'VALID' : 'INVALID');

                            // Try creating a blob URL as fallback
                            if (!isValidJPEG) {
                              try {
                                const blob = new Blob([bytes], { type: 'image/jpeg' });
                                const blobUrl = URL.createObjectURL(blob);
                                console.log('Editor: Created Blob URL:', blobUrl);
                                img.src = blobUrl;
                                return; // Don't hide the image yet, try the blob URL
                              } catch (blobError) {
                                console.error('Editor: Blob creation failed:', blobError);
                              }
                            }
                          } catch (decodeError) {
                            console.error('Editor: Base64 decode failed:', decodeError);
                          }
                        }
                      }

                      // Hide the image if all attempts failed
                      console.log('Editor: Image failed after fallback attempt, hiding image');
                      img.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Editor: Image loaded successfully');
                    }}
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="tag1, tag2, tag3"
                className="form-input"
              />
            </div>

            <div className="utility-editor-section">
              <h3>Utility Card (Hero Card)</h3>
              <p className="section-help">These fields power the premium Problem-Solution card at the top of your post.</p>

              <div className="form-group">
                <label htmlFor="uc_problem">The Problem</label>
                <textarea
                  id="uc_problem"
                  name="uc_problem"
                  value={formData.uc_problem}
                  onChange={handleInputChange}
                  placeholder="What is the primary pain point?"
                  className="form-textarea"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="uc_solution">The Consensus Solution</label>
                <textarea
                  id="uc_solution"
                  name="uc_solution"
                  value={formData.uc_solution}
                  onChange={handleInputChange}
                  placeholder="What is the community-vetted fix?"
                  className="form-textarea"
                  rows={2}
                />
              </div>

              <div className="utility-product-grid">
                <div className="form-group">
                  <label htmlFor="uc_product_name">Product Name</label>
                  <input
                    type="text"
                    id="uc_product_name"
                    name="uc_product_name"
                    value={formData.uc_product_name}
                    onChange={handleInputChange}
                    placeholder="e.g. Creality Ender 3"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="uc_product_url">Amazon URL (Affiliate)</label>
                  <input
                    type="url"
                    id="uc_product_url"
                    name="uc_product_url"
                    value={formData.uc_product_url}
                    onChange={handleInputChange}
                    placeholder="https://amazon.com/..."
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="uc_product_image_url">Product Image URL</label>
                  <input
                    type="url"
                    id="uc_product_image_url"
                    name="uc_product_image_url"
                    value={formData.uc_product_image_url}
                    onChange={handleInputChange}
                    placeholder="Image URL for the tool..."
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="uc_source_subreddit">Source Subreddit (for attribution)</label>
                  <input
                    type="text"
                    id="uc_source_subreddit"
                    name="uc_source_subreddit"
                    value={formData.uc_source_subreddit}
                    onChange={handleInputChange}
                    placeholder="e.g. homeautomation or smarthome"
                    className="form-input"
                  />
                  <small className="form-hint">Shows &quot;Based on r/homeautomation consensus&quot; on the card. Leave blank for generic Reddit attribution.</small>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="body">Content</label>
              <RichEditor
                content={formData.body}
                onChange={(content) => setFormData(prev => ({ ...prev, body: content }))}
                placeholder="Write your post content here..."
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Editor;
